import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  FiTrendingUp, FiBarChart2, FiPackage, FiAlertTriangle,
  FiUsers, FiShoppingBag, FiRefreshCw, FiZap, FiTarget,
  FiActivity, FiPieChart, FiStar, FiArrowUp, FiArrowDown,
  FiClock, FiCheckCircle, FiAlertCircle, FiInfo,
  FiChevronRight, FiLayers, FiBell, FiSliders, FiCpu,
  FiDatabase, FiWifiOff
} from "react-icons/fi";
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, Legend
} from "recharts";
import Breadcrumb from "../../components/common/Breadcrumb";
import { API_URL } from "../../config";
import axios from "axios";

const COLORS = ["#3b82f6","#10b981","#f59e0b","#ef4444","#8b5cf6","#06b6d4","#ec4899","#14b8a6"];
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DAYS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

const fmt  = (v) => `Rs. ${Number(v||0).toLocaleString("en-IN",{maximumFractionDigits:0})}`;
const fmtN = (v) => Number(v||0).toLocaleString("en-IN");
const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;

const parseDate = (inv) => {
  const raw = inv.INDATE || inv.INNOVICED_ON || inv.INVOICEDATE || inv.DATE;
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d) ? null : d;
};

const chartTooltipStyle = (dark) => ({
  contentStyle: {
    backgroundColor: dark ? "#1f2937" : "#fff",
    border: `1px solid ${dark?"#374151":"#e5e7eb"}`,
    borderRadius: "8px", fontSize: "11px", padding: "6px 10px",
  },
  labelStyle: { color: dark ? "#d1d5db" : "#374151", fontSize: "11px" },
});

const AIBadge = () => (
  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 text-white">
    <FiCpu className="w-3 h-3" /> AI
  </span>
);
const LiveBadge = () => (
  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
    <FiDatabase className="w-3 h-3" /> Live Data
  </span>
);

const StatCard = ({ icon: Icon, label, value, sub, color, dark }) => (
  <div className={`rounded-xl p-4 border flex items-start gap-3 shadow-sm ${dark?"bg-gray-800 border-gray-700":"bg-white border-gray-200"}`}>
    <div className={`p-2 rounded-lg ${color}`}><Icon className="w-5 h-5 text-white" /></div>
    <div className="min-w-0">
      <p className={`text-xs font-medium ${dark?"text-gray-400":"text-gray-500"}`}>{label}</p>
      <p className={`text-lg font-bold truncate ${dark?"text-white":"text-gray-900"}`}>{value}</p>
      {sub && <p className={`text-xs mt-0.5 ${dark?"text-gray-500":"text-gray-400"}`}>{sub}</p>}
    </div>
  </div>
);

const SectionCard = ({ title, subtitle, icon: Icon, iconColor, children, dark, badge, extra }) => (
  <div className={`rounded-xl border shadow-sm overflow-hidden ${dark?"bg-gray-800 border-gray-700":"bg-white border-gray-200"}`}>
    <div className={`px-5 py-4 flex items-center justify-between border-b ${dark?"border-gray-700":"border-gray-100"}`}>
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${iconColor}`}><Icon className="w-4 h-4 text-white" /></div>
        <div>
          <h3 className={`font-bold text-sm ${dark?"text-white":"text-gray-900"}`}>{title}</h3>
          {subtitle && <p className={`text-xs ${dark?"text-gray-400":"text-gray-500"}`}>{subtitle}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {extra}
        {badge && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">{badge}</span>}
      </div>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const Spinner = ({ dark }) => (
  <div className="flex items-center justify-center py-16">
    <div className="flex flex-col items-center gap-3">
      <FiRefreshCw className={`w-8 h-8 animate-spin ${dark?"text-blue-400":"text-blue-500"}`} />
      <p className={`text-sm ${dark?"text-gray-400":"text-gray-500"}`}>Loading your POS data…</p>
    </div>
  </div>
);

const NoData = ({ dark, message = "No data available" }) => (
  <div className={`flex flex-col items-center justify-center py-10 ${dark?"text-gray-500":"text-gray-400"}`}>
    <FiDatabase className="w-8 h-8 mb-2 opacity-40" />
    <p className="text-sm">{message}</p>
  </div>
);

// ── Data hook: fetches all 3 APIs in parallel ─────────────────────────────────
const usePOSData = () => {
  const [state, setState] = useState({
    invoices: [], products: [], customers: [],
    loading: true, errors: [], lastUpdated: null,
  });

  const load = useCallback(async () => {
    setState(s => ({ ...s, loading: true, errors: [] }));
    const token = localStorage.getItem("token");
    const headers = { "auth-key": token };
    const today = new Date();
    const start = new Date(today); start.setDate(today.getDate() - 90);

    const [invRes, prodRes, cusRes] = await Promise.allSettled([
      axios.get(`${API_URL}/SRD/GetInvoice?p_start_date=${fmtDate(start)}&p_end_date=${fmtDate(today)}`, { headers }),
      axios.get(`${API_URL}/ProductItem/GetAllActiveProduct`, { headers }),
      axios.get(`${API_URL}/Customer/GetAllCustomers`, { headers }),
    ]);

    const errors = [];
    const invoices  = invRes.status  === "fulfilled" ? (invRes.value.data?.ResultSet  || []) : (errors.push("Sales"), []);
    const products  = prodRes.status === "fulfilled" ? (prodRes.value.data?.ResultSet || []) : (errors.push("Products"), []);
    const customers = cusRes.status  === "fulfilled" ? (cusRes.value.data?.ResultSet  || []) : (errors.push("Customers"), []);

    setState({ invoices, products, customers, loading: false, errors, lastUpdated: new Date() });
  }, []);

  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
};

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 1 — Sales Forecasting
// ═══════════════════════════════════════════════════════════════════════════════
const SalesForecastingTab = ({ dark, invoices, products, loading }) => {
  const [whatIfDiscount, setWhatIfDiscount] = useState(10);
  const [whatIfCategory, setWhatIfCategory] = useState("all");
  const [whatIfResult, setWhatIfResult]     = useState(null);
  const [simulating, setSimulating]         = useState(false);

  const forecastData = React.useMemo(() => {
    const byMonth = {};
    invoices.forEach(inv => {
      const d = parseDate(inv); if (!d) return;
      const m = MONTHS[d.getMonth()];
      byMonth[m] = (byMonth[m] || 0) + (parseFloat(inv.SALESVALUE) || 0);
    });
    const nowMonth = new Date().getMonth();
    const vals = Object.values(byMonth);
    const avg = vals.length ? vals.reduce((a,b)=>a+b,0)/vals.length : 0;
    return MONTHS.map((m, i) => {
      const actual = byMonth[m] || null;
      const seasonal = 1 + 0.15 * Math.sin((i - 2) * Math.PI / 6);
      return {
        month: m,
        actual: actual ? Math.round(actual) : (i <= nowMonth ? 0 : null),
        forecast: Math.round(avg * seasonal),
        lower:    Math.round(avg * seasonal * 0.88),
        upper:    Math.round(avg * seasonal * 1.12),
      };
    });
  }, [invoices]);

  const totalRevenue = invoices.reduce((s,i) => s+(parseFloat(i.SALESVALUE)||0), 0);
  const totalProfit  = invoices.reduce((s,i) => s+(parseFloat(i.TOTAL_PROFIT)||0), 0);
  const avgDaily     = totalRevenue / 90;
  const nextMonthFC  = forecastData[Math.min(new Date().getMonth()+1, 11)]?.forecast || 0;

  const demandItems = React.useMemo(() => {
    const byProd = {};
    invoices.forEach(inv => {
      const name = inv.PRDESC || "Unknown";
      if (!byProd[name]) byProd[name] = { qty:0, revenue:0, days:new Set() };
      byProd[name].qty     += parseFloat(inv.SOLDQTY)    || 0;
      byProd[name].revenue += parseFloat(inv.SALESVALUE) || 0;
      const d = parseDate(inv); if (d) byProd[name].days.add(fmtDate(d));
    });
    return Object.entries(byProd)
      .map(([name,v]) => ({
        name,
        forecastQty: Math.round((v.qty / 90) * 30),
        confidence: Math.min(99, Math.round(60 + v.days.size * 0.8)),
        trend: v.qty > 30 ? "up" : "down",
        revenue: v.revenue,
      }))
      .sort((a,b) => b.revenue - a.revenue)
      .slice(0, 6);
  }, [invoices]);

  const seasonalAlerts = React.useMemo(() =>
    demandItems.slice(0,3).map((item,i) => ({
      item: item.name,
      expected: `+${Math.round(15+i*10)}%`,
      urgency: i===0?"high":i===1?"medium":"low",
      daysLeft: Math.round(7+i*12),
    }))
  , [demandItems]);

  const categories = React.useMemo(() => {
    const cats = [...new Set(products.map(p => p.PCATNAME || p.PCATDESC || "").filter(Boolean))];
    return cats.slice(0,8);
  }, [products]);

  const runWhatIf = () => {
    setSimulating(true);
    setTimeout(() => {
      const base = avgDaily * 30;
      const revenueChange = base * (whatIfDiscount/100) * (1 + (-1.3) * (whatIfDiscount/100));
      setWhatIfResult({
        baseRevenue:       Math.round(base),
        projectedRevenue:  Math.round(base + revenueChange),
        unitIncrease:      Math.round((whatIfDiscount/100)*1.8*(invoices.length/3)),
        netImpact:         Math.round(revenueChange),
      });
      setSimulating(false);
    }, 1200);
  };

  if (loading) return <Spinner dark={dark} />;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={FiTrendingUp} label="Next Month Forecast" value={fmt(nextMonthFC)}   sub="AI projected revenue"      color="bg-blue-500"   dark={dark} />
        <StatCard icon={FiTarget}     label="90-Day Revenue"       value={fmt(totalRevenue)} sub="From your POS invoices"    color="bg-green-500"  dark={dark} />
        <StatCard icon={FiActivity}   label="Avg Daily Revenue"    value={fmt(avgDaily)}     sub="Last 90 days average"      color="bg-purple-500" dark={dark} />
        <StatCard icon={FiStar}       label="Total Profit (90d)"   value={fmt(totalProfit)}  sub="Net profit from invoices"  color="bg-amber-500"  dark={dark} />
      </div>

      <SectionCard title="Revenue Forecast — 12 Months" subtitle="Actual (your POS) vs AI-predicted with confidence band" icon={FiTrendingUp} iconColor="bg-blue-500" dark={dark} badge="Predictive AI" extra={<LiveBadge />}>
        <div className="flex items-center gap-4 mb-4 flex-wrap">
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-blue-500" /><span className={`text-xs ${dark?"text-gray-300":"text-gray-600"}`}>Actual</span></div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-purple-400" /><span className={`text-xs ${dark?"text-gray-300":"text-gray-600"}`}>Forecast</span></div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-purple-200 opacity-60" /><span className={`text-xs ${dark?"text-gray-300":"text-gray-600"}`}>Confidence Band</span></div>
          <AIBadge />
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={forecastData} margin={{top:5,right:10,left:0,bottom:0}}>
            <defs>
              <linearGradient id="bandGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="#8b5cf6" stopOpacity={0.15}/>
                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.02}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={dark?"#374151":"#f3f4f6"}/>
            <XAxis dataKey="month" stroke={dark?"#6b7280":"#9ca3af"} fontSize={11}/>
            <YAxis stroke={dark?"#6b7280":"#9ca3af"} fontSize={10} tickFormatter={v=>`${(v/1000).toFixed(0)}k`}/>
            <Tooltip {...chartTooltipStyle(dark)} formatter={v=>[fmt(v)]}/>
            <Area type="monotone" dataKey="upper" stroke="none" fill="url(#bandGrad)" stackId="band"/>
            <Area type="monotone" dataKey="lower" stroke="none" fill={dark?"#1f2937":"#fff"} stackId="band"/>
            <Line type="monotone" dataKey="actual"   stroke="#3b82f6" strokeWidth={2.5} dot={{r:3,fill:"#3b82f6"}} connectNulls={false} name="Actual"/>
            <Line type="monotone" dataKey="forecast" stroke="#8b5cf6" strokeWidth={2} strokeDasharray="6 3" dot={{r:3,fill:"#8b5cf6"}} name="Forecast"/>
          </AreaChart>
        </ResponsiveContainer>
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Demand Forecast by Product" subtitle="Next 30-day unit projections from 90-day sales data" icon={FiPackage} iconColor="bg-green-500" dark={dark} extra={<LiveBadge />}>
          {demandItems.length===0 ? <NoData dark={dark} message="No sales data to forecast from"/> : (
            <div className="space-y-3">
              {demandItems.map((item,i)=>(
                <div key={i} className={`flex items-center justify-between p-3 rounded-lg ${dark?"bg-gray-700/50":"bg-gray-50"}`}>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${dark?"text-white":"text-gray-800"}`}>{item.name}</p>
                    <p className={`text-xs ${dark?"text-gray-400":"text-gray-500"}`}>Revenue: {fmt(item.revenue)}</p>
                  </div>
                  <div className="text-right ml-3 shrink-0">
                    <div className="flex items-center gap-1.5 justify-end">
                      <span className={`text-base font-bold ${dark?"text-white":"text-gray-900"}`}>{item.forecastQty}</span>
                      <span className={`text-xs ${dark?"text-gray-400":"text-gray-500"}`}>units</span>
                      {item.trend==="up"?<FiArrowUp className="w-4 h-4 text-green-500"/>:<FiArrowDown className="w-4 h-4 text-red-400"/>}
                    </div>
                    <div className="flex items-center gap-1 justify-end mt-0.5">
                      <div className="h-1.5 rounded-full bg-gray-200 w-16"><div className="h-1.5 rounded-full bg-blue-500" style={{width:`${item.confidence}%`}}/></div>
                      <span className={`text-xs ${dark?"text-gray-400":"text-gray-500"}`}>{item.confidence}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard title="Seasonal Trend Alerts" subtitle="High-velocity products — plan stock in advance" icon={FiBell} iconColor="bg-amber-500" dark={dark} extra={<LiveBadge />}>
          {seasonalAlerts.length===0 ? <NoData dark={dark} message="No trend data yet"/> : (
            <div className="space-y-3">
              {seasonalAlerts.map((alert,i)=>(
                <div key={i} className={`p-4 rounded-xl border-l-4 ${alert.urgency==="high"?"border-red-500 bg-red-50 dark:bg-red-900/20":alert.urgency==="medium"?"border-amber-500 bg-amber-50 dark:bg-amber-900/20":"border-blue-400 bg-blue-50 dark:bg-blue-900/20"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className={`text-sm font-semibold ${dark?"text-white":"text-gray-900"}`}>{alert.item}</p>
                      <p className={`text-xs mt-0.5 ${dark?"text-gray-400":"text-gray-600"}`}>High demand product</p>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-sm font-bold text-green-600">{alert.expected}</span>
                      <p className={`text-xs ${dark?"text-gray-400":"text-gray-500"}`}>{alert.daysLeft} days</p>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-1.5">
                    <FiClock className={`w-3 h-3 ${alert.urgency==="high"?"text-red-500":"text-amber-500"}`}/>
                    <p className={`text-xs font-medium ${alert.urgency==="high"?"text-red-600":"text-amber-600"}`}>
                      Increase stock {alert.daysLeft<=14?"IMMEDIATELY":`within ${alert.daysLeft} days`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      <SectionCard title="What-If Scenario Simulator" subtitle="Simulate how a discount affects your next 30-day revenue using real sales data" icon={FiSliders} iconColor="bg-purple-500" dark={dark} badge="Interactive AI">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${dark?"text-gray-300":"text-gray-700"}`}>
                Discount: <span className="font-bold text-purple-600">{whatIfDiscount}%</span>
              </label>
              <input type="range" min="1" max="50" value={whatIfDiscount}
                onChange={e=>{setWhatIfDiscount(+e.target.value);setWhatIfResult(null);}}
                className="w-full accent-purple-500"/>
              <div className="flex justify-between text-xs mt-1 text-gray-400"><span>1%</span><span>25%</span><span>50%</span></div>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${dark?"text-gray-300":"text-gray-700"}`}>Apply To Category</label>
              <select value={whatIfCategory} onChange={e=>{setWhatIfCategory(e.target.value);setWhatIfResult(null);}}
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 ${dark?"bg-gray-700 border-gray-600 text-white":"bg-white border-gray-300 text-gray-900"}`}>
                <option value="all">All Products</option>
                {categories.map((c,i)=><option key={i} value={c}>{c}</option>)}
              </select>
            </div>
            <button onClick={runWhatIf} disabled={simulating||invoices.length===0}
              className="w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white font-semibold py-2.5 px-4 rounded-lg text-sm transition-colors">
              {simulating?<FiRefreshCw className="w-4 h-4 animate-spin"/>:<FiZap className="w-4 h-4"/>}
              {simulating?"Simulating…":invoices.length===0?"Waiting for sales data…":"Run Simulation"}
            </button>
          </div>
          <div>
            {!whatIfResult&&!simulating&&(
              <div className={`h-full flex flex-col items-center justify-center rounded-xl border-2 border-dashed py-10 ${dark?"border-gray-600 text-gray-500":"border-gray-200 text-gray-400"}`}>
                <FiZap className="w-10 h-10 mb-3 opacity-40"/>
                <p className="text-sm font-medium">Set parameters and run simulation</p>
                <p className="text-xs mt-1">Uses your real 90-day sales figures</p>
              </div>
            )}
            {simulating&&(
              <div className={`h-full flex flex-col items-center justify-center rounded-xl ${dark?"bg-gray-700/40":"bg-purple-50"}`}>
                <FiCpu className="w-10 h-10 text-purple-500 animate-pulse mb-3"/>
                <p className={`text-sm font-semibold ${dark?"text-white":"text-gray-700"}`}>Calculating impact…</p>
              </div>
            )}
            {whatIfResult&&!simulating&&(
              <div className="space-y-3">
                <p className={`text-xs font-semibold uppercase tracking-wide ${dark?"text-gray-400":"text-gray-500"}`}>Simulation Results</p>
                <div className={`p-4 rounded-xl ${dark?"bg-gray-700/50":"bg-gray-50"} space-y-3`}>
                  {[
                    {label:"Base Revenue (30 days)",val:fmt(whatIfResult.baseRevenue),color:"text-gray-600"},
                    {label:"Projected Revenue",val:fmt(whatIfResult.projectedRevenue),color:"text-blue-600 font-bold"},
                    {label:"Extra Units Sold",val:`+${fmtN(whatIfResult.unitIncrease)} units`,color:"text-green-600 font-bold"},
                    {label:"Net Revenue Impact",val:`${whatIfResult.netImpact>=0?"+":""}${fmt(whatIfResult.netImpact)}`,color:whatIfResult.netImpact>=0?"text-green-600 font-bold":"text-red-500 font-bold"},
                  ].map((row,i)=>(
                    <div key={i} className="flex justify-between items-center">
                      <span className={`text-xs ${dark?"text-gray-400":"text-gray-600"}`}>{row.label}</span>
                      <span className={`text-sm ${row.color}`}>{row.val}</span>
                    </div>
                  ))}
                </div>
                <div className={`p-3 rounded-lg flex gap-2 ${dark?"bg-blue-900/30":"bg-blue-50"}`}>
                  <FiInfo className="w-4 h-4 text-blue-500 shrink-0 mt-0.5"/>
                  <p className="text-xs text-blue-600">A {whatIfDiscount}% discount is estimated to increase volume by ~{Math.round(whatIfDiscount*1.8)}% but reduce per-unit margin. Monitor weekly.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </SectionCard>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 2 — Sales Analysis
// ═══════════════════════════════════════════════════════════════════════════════
const SalesAnalysisTab = ({ dark, invoices, customers, loading }) => {

  const salesByDay = React.useMemo(() => {
    const map = {};
    invoices.forEach(inv => {
      const d = parseDate(inv); if (!d) return;
      const key = DAYS[d.getDay()];
      if (!map[key]) map[key] = {sales:0,transactions:0};
      map[key].sales += parseFloat(inv.SALESVALUE)||0;
      map[key].transactions += 1;
    });
    return ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => ({
      day:d, sales:Math.round(map[d]?.sales||0), transactions:map[d]?.transactions||0
    }));
  }, [invoices]);

  const cashierPerf = React.useMemo(() => {
    const map = {};
    invoices.forEach(inv => {
      const name = inv.CAHIERNAME || "Unknown";
      if (!map[name]) map[name] = {revenue:0,transactions:0,profit:0};
      map[name].revenue      += parseFloat(inv.SALESVALUE)  ||0;
      map[name].transactions += 1;
      map[name].profit       += parseFloat(inv.TOTAL_PROFIT)||0;
    });
    return Object.entries(map)
      .map(([name,v]) => ({name,...v,avg:v.transactions?v.revenue/v.transactions:0}))
      .sort((a,b) => b.revenue-a.revenue).slice(0,6);
  }, [invoices]);

  const segments = React.useMemo(() => {
    if (!customers.length) return [];
    const bronze = customers.filter(c=>c.CUS_TIERLEVEL==="Bronze").length;
    const silver = customers.filter(c=>c.CUS_TIERLEVEL==="Silver").length;
    const gold   = customers.filter(c=>c.CUS_TIERLEVEL==="Gold").length;
    const other  = customers.length - bronze - silver - gold;
    return [
      {name:"Gold Members",   count:gold,   color:COLORS[2], pct:Math.round(gold/customers.length*100),   desc:"Highest loyalty tier"},
      {name:"Silver Members", count:silver, color:COLORS[5], pct:Math.round(silver/customers.length*100), desc:"Mid loyalty tier"},
      {name:"Bronze Members", count:bronze, color:COLORS[0], pct:Math.round(bronze/customers.length*100), desc:"Entry loyalty tier"},
      {name:"Untiered",       count:other,  color:COLORS[3], pct:Math.round(other/customers.length*100),  desc:"No tier assigned"},
    ].filter(s=>s.count>0);
  }, [customers]);

  const churnRisk = React.useMemo(() => {
    if (!customers.length||!invoices.length) return [];
    const activeNames = new Set(invoices.map(i=>i.CUSNAME).filter(Boolean));
    return customers
      .filter(c=>c.CUS_NAME&&!activeNames.has(c.CUS_NAME)&&(c.CUS_POINTSBALANCE>0||c.CUS_TIERLEVEL==="Gold"||c.CUS_TIERLEVEL==="Silver"))
      .sort((a,b)=>(b.CUS_POINTSBALANCE||0)-(a.CUS_POINTSBALANCE||0))
      .slice(0,5)
      .map(c=>({
        name:c.CUS_NAME,phone:c.CUS_PHONE||"-",
        tier:c.CUS_TIERLEVEL||"None",points:c.CUS_POINTSBALANCE||0,
        risk:c.CUS_TIERLEVEL==="Gold"?"High":"Medium",
      }));
  }, [customers, invoices]);

  const topProducts = React.useMemo(() => {
    const map = {};
    invoices.forEach(inv => {
      const name = inv.PRDESC||"Unknown";
      if (!map[name]) map[name] = {revenue:0,qty:0};
      map[name].revenue += parseFloat(inv.SALESVALUE)||0;
      map[name].qty     += parseFloat(inv.SOLDQTY)   ||0;
    });
    return Object.entries(map).sort((a,b)=>b[1].revenue-a[1].revenue).slice(0,5)
      .map(([name,v],i)=>({name,...v,color:COLORS[i]}));
  }, [invoices]);

  const uniqueCustomers = new Set(invoices.map(i=>i.CUSNAME).filter(Boolean)).size;

  if (loading) return <Spinner dark={dark}/>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={FiUsers}       label="Total Customers"     value={fmtN(customers.length)}     sub="In your customer database"    color="bg-blue-500"   dark={dark}/>
        <StatCard icon={FiAlertCircle} label="Churn Risk"          value={`${churnRisk.length} Customers`} sub="Inactive loyalty holders" color="bg-red-500"    dark={dark}/>
        <StatCard icon={FiShoppingBag} label="Active Buyers (90d)" value={fmtN(uniqueCustomers)}     sub="Unique customers in invoices" color="bg-green-500"  dark={dark}/>
        <StatCard icon={FiStar}        label="Cashiers Tracked"    value={fmtN(cashierPerf.length)}  sub="Performance monitored"        color="bg-purple-500" dark={dark}/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Sales by Day of Week" subtitle="Revenue pattern from your 90-day POS data" icon={FiBarChart2} iconColor="bg-purple-500" dark={dark} extra={<LiveBadge/>}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={salesByDay} margin={{top:5,right:5,left:0,bottom:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke={dark?"#374151":"#f3f4f6"}/>
              <XAxis dataKey="day" stroke={dark?"#6b7280":"#9ca3af"} fontSize={11}/>
              <YAxis stroke={dark?"#6b7280":"#9ca3af"} fontSize={10} tickFormatter={v=>`${(v/1000).toFixed(0)}k`}/>
              <Tooltip {...chartTooltipStyle(dark)} formatter={(v,n)=>[n==="sales"?fmt(v):v,n==="sales"?"Revenue":"Transactions"]}/>
              <Bar dataKey="sales" fill="#8b5cf6" radius={[4,4,0,0]} name="sales"/>
            </BarChart>
          </ResponsiveContainer>
        </SectionCard>

        <SectionCard title="Top Products by Revenue" subtitle="Best performing products — last 90 days" icon={FiTrendingUp} iconColor="bg-green-500" dark={dark} extra={<LiveBadge/>}>
          {topProducts.length===0?<NoData dark={dark}/>:(
            <div className="space-y-2">
              {topProducts.map((p,i)=>(
                <div key={i} className={`flex items-center gap-3 p-2 rounded-lg ${dark?"hover:bg-gray-700/30":"hover:bg-gray-50"}`}>
                  <div className="w-3 h-3 rounded-full shrink-0" style={{backgroundColor:p.color}}/>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${dark?"text-white":"text-gray-800"}`}>{p.name}</p>
                    <div className="mt-0.5 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700">
                      <div className="h-1.5 rounded-full" style={{width:`${Math.round((p.revenue/topProducts[0].revenue)*100)}%`,backgroundColor:p.color}}/>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-sm font-bold ${dark?"text-white":"text-gray-900"}`}>{fmt(p.revenue)}</p>
                    <p className={`text-xs ${dark?"text-gray-400":"text-gray-500"}`}>{fmtN(p.qty)} units</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Customer Segmentation" subtitle="Loyalty tier breakdown from your customer database" icon={FiUsers} iconColor="bg-blue-500" dark={dark} badge="ML Clustering" extra={<LiveBadge/>}>
          {segments.length===0?<NoData dark={dark} message="No customer data loaded"/>:(
            <div>
              <ResponsiveContainer width="100%" height={150}>
                <PieChart>
                  <Pie data={segments.map(s=>({name:s.name,value:s.count}))} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" paddingAngle={3}>
                    {segments.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
                  </Pie>
                  <Tooltip {...chartTooltipStyle(dark)} formatter={(v,n)=>[`${v} customers`,n]}/>
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-2">
                {segments.map((seg,i)=>(
                  <div key={i} className={`p-3 rounded-lg flex items-center gap-3 ${dark?"bg-gray-700/50":"bg-gray-50"}`}>
                    <div className="w-3 h-3 rounded-full shrink-0" style={{backgroundColor:seg.color}}/>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center">
                        <span className={`text-sm font-semibold ${dark?"text-white":"text-gray-800"}`}>{seg.name}</span>
                        <span className={`text-xs ${dark?"text-gray-400":"text-gray-500"}`}>{seg.count} ({seg.pct}%)</span>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-gray-200 dark:bg-gray-600">
                        <div className="h-1.5 rounded-full" style={{width:`${seg.pct}%`,backgroundColor:seg.color}}/>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </SectionCard>

        <SectionCard title="Churn Risk — Loyalty Customers" subtitle="Loyalty customers not seen in recent 90-day invoices" icon={FiAlertCircle} iconColor="bg-red-500" dark={dark} badge="Predictive AI" extra={<LiveBadge/>}>
          {churnRisk.length===0?(
            <div className="flex flex-col items-center justify-center py-8">
              <FiCheckCircle className="w-8 h-8 mb-2 text-green-500"/>
              <p className="text-sm font-medium text-green-600">All loyalty customers are active!</p>
            </div>
          ):(
            <div className="space-y-2">
              {churnRisk.map((c,i)=>(
                <div key={i} className={`flex items-center gap-3 p-3 rounded-xl border ${dark?"bg-gray-700/40 border-gray-600":"bg-gray-50 border-gray-200"}`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0 ${c.risk==="High"?"bg-red-500":"bg-amber-400"}`}>{c.name.charAt(0)}</div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${dark?"text-white":"text-gray-800"}`}>{c.name}</p>
                    <p className={`text-xs ${dark?"text-gray-400":"text-gray-500"}`}>{c.phone} · {c.points} pts · {c.tier}</p>
                  </div>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full shrink-0 ${c.risk==="High"?"bg-red-100 text-red-600":"bg-amber-100 text-amber-600"}`}>{c.risk}</span>
                </div>
              ))}
              <div className={`mt-2 p-3 rounded-lg flex gap-2 ${dark?"bg-blue-900/20":"bg-blue-50"}`}>
                <FiInfo className="w-4 h-4 text-blue-500 shrink-0 mt-0.5"/>
                <p className="text-xs text-blue-600">Consider sending a loyalty discount via SMS to win these customers back.</p>
              </div>
            </div>
          )}
        </SectionCard>
      </div>

      <SectionCard title="Cashier Performance" subtitle="Revenue, transactions and profit per cashier — last 90 days" icon={FiActivity} iconColor="bg-indigo-500" dark={dark} extra={<LiveBadge/>}>
        {cashierPerf.length===0?<NoData dark={dark}/>:(
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-sm min-w-[480px]">
              <thead>
                <tr className={`text-xs uppercase tracking-wide ${dark?"text-gray-400":"text-gray-500"}`}>
                  <th className="text-left py-2 font-semibold">Cashier</th>
                  <th className="text-right py-2 font-semibold">Transactions</th>
                  <th className="text-right py-2 font-semibold">Revenue</th>
                  <th className="text-right py-2 font-semibold">Avg / Sale</th>
                  <th className="text-right py-2 font-semibold">Profit</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${dark?"divide-gray-700":"divide-gray-100"}`}>
                {cashierPerf.map((c,i)=>(
                  <tr key={i} className={`${dark?"hover:bg-gray-700/30":"hover:bg-gray-50"} transition-colors`}>
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{backgroundColor:COLORS[i%COLORS.length]}}>{c.name.charAt(0)}</div>
                        <span className={`font-medium ${dark?"text-white":"text-gray-800"}`}>{c.name}</span>
                        {i===0&&<span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full font-semibold">Top</span>}
                      </div>
                    </td>
                    <td className={`text-right py-3 ${dark?"text-gray-300":"text-gray-700"}`}>{fmtN(c.transactions)}</td>
                    <td className={`text-right py-3 font-semibold ${dark?"text-white":"text-gray-900"}`}>{fmt(c.revenue)}</td>
                    <td className={`text-right py-3 ${dark?"text-gray-300":"text-gray-700"}`}>{fmt(c.avg)}</td>
                    <td className="text-right py-3 text-green-600 font-semibold">{fmt(c.profit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 3 — Inventory Analysis
// ═══════════════════════════════════════════════════════════════════════════════
const InventoryAnalysisTab = ({ dark, invoices, products, loading }) => {

  const reorderItems = React.useMemo(() => {
    return products
      .map(p => ({
        name:         p.PPDES      || "Unknown",
        sku:          p.PPROCODE   || "-",
        current:      parseFloat(p.PBALQTY)   || 0,
        reorderPoint: parseFloat(p.PREOLEVEL) || 0,
        status:       parseFloat(p.PBALQTY)<=0 ? "critical"
                    : parseFloat(p.PBALQTY)<=parseFloat(p.PREOLEVEL) ? "low"
                    : "ok",
      }))
      .filter(p => p.reorderPoint > 0)
      .sort((a,b) => {const r={critical:0,low:1,ok:2}; return (r[a.status]||3)-(r[b.status]||3);})
      .slice(0,8);
  }, [products]);

  const deadstock = React.useMemo(() =>
    products.filter(p=>parseFloat(p.PBALQTY)<=5&&parseFloat(p.PBALQTY)>=0).slice(0,5)
      .map(p=>({
        name:p.PPDES||"Unknown", sku:p.PPROCODE||"-",
        qty:parseFloat(p.PBALQTY)||0,
        suggest:parseFloat(p.PBALQTY)===0?"Reorder urgently":"Top up stock",
      }))
  , [products]);

  const productVelocity = React.useMemo(() => {
    const map = {};
    invoices.forEach(inv => {
      const name = inv.PRDESC||"Unknown";
      if (!map[name]) map[name]={qty:0,revenue:0};
      map[name].qty     += parseFloat(inv.SOLDQTY)   ||0;
      map[name].revenue += parseFloat(inv.SALESVALUE)||0;
    });
    return Object.entries(map).sort((a,b)=>b[1].qty-a[1].qty).slice(0,6)
      .map(([name,v])=>({name,velocity:Math.round(v.qty/90*10)/10,totalQty:v.qty,revenue:v.revenue}));
  }, [invoices]);

  const radarData = React.useMemo(() => {
    const total     = products.length || 1;
    const critical  = products.filter(p=>parseFloat(p.PBALQTY)<=0).length;
    const low       = products.filter(p=>{const b=parseFloat(p.PBALQTY)||0,r=parseFloat(p.PREOLEVEL)||0; return r>0&&b<=r&&b>0;}).length;
    const healthy   = total - critical - low;
    const reoSetup  = products.filter(p=>parseFloat(p.PREOLEVEL)>0).length;
    return [
      {metric:"Stock Health",      score:Math.round(healthy/total*100)},
      {metric:"Reorder Setup",     score:Math.round(reoSetup/total*100)},
      {metric:"Critical Control",  score:Math.max(0,100-Math.round(critical/total*100)*5)},
      {metric:"Product Range",     score:Math.min(99,total)},
      {metric:"Sales Coverage",    score:Math.min(95,productVelocity.length*15)},
      {metric:"Low Stock Control", score:Math.max(0,100-Math.round(low/total*100)*3)},
    ];
  }, [products, productVelocity]);

  const criticalCount = reorderItems.filter(i=>i.status==="critical").length;
  const lowCount      = reorderItems.filter(i=>i.status==="low").length;
  const statusColor = s => s==="critical"?"text-red-500 bg-red-100":s==="low"?"text-amber-600 bg-amber-100":"text-green-600 bg-green-100";
  const statusLabel = s => s==="critical"?"Critical":s==="low"?"Low":"OK";

  if (loading) return <Spinner dark={dark}/>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={FiAlertTriangle} label="Critical Stock"     value={`${criticalCount} SKUs`}     sub="Zero or below reorder"        color="bg-red-500"    dark={dark}/>
        <StatCard icon={FiPackage}       label="Low Stock"          value={`${lowCount} SKUs`}           sub="At or below reorder level"    color="bg-amber-500"  dark={dark}/>
        <StatCard icon={FiLayers}        label="Total Active Items" value={fmtN(products.length)}        sub="Active product catalogue"     color="bg-blue-500"   dark={dark}/>
        <StatCard icon={FiActivity}      label="Top Velocity"       value={`${productVelocity[0]?.velocity||0}/day`} sub={productVelocity[0]?.name?.slice(0,20)||"N/A"} color="bg-purple-500" dark={dark}/>
      </div>

      <SectionCard title="Smart Reorder Alerts" subtitle="Real-time balance qty vs reorder points from your product catalogue" icon={FiSliders} iconColor="bg-blue-500" dark={dark} badge="Live Stock" extra={<LiveBadge/>}>
        {reorderItems.length===0?<NoData dark={dark} message="No reorder levels configured in product catalogue"/>:(
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-sm min-w-[520px]">
              <thead>
                <tr className={`text-xs uppercase tracking-wide ${dark?"text-gray-400":"text-gray-500"}`}>
                  <th className="text-left py-2 font-semibold">Product</th>
                  <th className="text-center py-2 font-semibold">Balance Qty</th>
                  <th className="text-center py-2 font-semibold">Reorder Level</th>
                  <th className="text-center py-2 font-semibold">Gap</th>
                  <th className="text-center py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${dark?"divide-gray-700":"divide-gray-100"}`}>
                {reorderItems.map((item,i)=>(
                  <tr key={i} className={`${dark?"hover:bg-gray-700/30":"hover:bg-gray-50"} transition-colors`}>
                    <td className="py-3">
                      <p className={`font-medium truncate max-w-[180px] ${dark?"text-white":"text-gray-800"}`}>{item.name}</p>
                      <p className={`text-xs ${dark?"text-gray-500":"text-gray-400"}`}>{item.sku}</p>
                    </td>
                    <td className={`text-center font-bold py-3 ${item.current<=item.reorderPoint?"text-red-500":dark?"text-white":"text-gray-800"}`}>{fmtN(item.current)}</td>
                    <td className={`text-center py-3 ${dark?"text-gray-300":"text-gray-700"}`}>{fmtN(item.reorderPoint)}</td>
                    <td className={`text-center py-3 font-medium ${item.reorderPoint>item.current?"text-red-500":"text-green-600"}`}>
                      {item.reorderPoint>item.current?`-${fmtN(item.reorderPoint-item.current)}`:`+${fmtN(item.current-item.reorderPoint)}`}
                    </td>
                    <td className="text-center py-3"><span className={`text-xs font-bold px-2.5 py-1 rounded-full ${statusColor(item.status)}`}>{statusLabel(item.status)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Zero / Near-Zero Stock" subtitle="Products at stockout risk — reorder now" icon={FiAlertCircle} iconColor="bg-amber-500" dark={dark} extra={<LiveBadge/>}>
          {deadstock.length===0?(
            <div className="flex flex-col items-center justify-center py-8">
              <FiCheckCircle className="w-8 h-8 mb-2 text-green-500"/>
              <p className="text-sm font-medium text-green-600">All products are adequately stocked!</p>
            </div>
          ):(
            <div className="space-y-3">
              {deadstock.map((item,i)=>(
                <div key={i} className={`p-3 rounded-xl border ${dark?"bg-gray-700/40 border-gray-600":"bg-amber-50/50 border-amber-200"}`}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <p className={`text-sm font-semibold truncate ${dark?"text-white":"text-gray-800"}`}>{item.name}</p>
                      <p className={`text-xs ${dark?"text-gray-400":"text-gray-500"}`}>{item.sku}</p>
                    </div>
                    <span className={`text-sm font-bold shrink-0 ${item.qty===0?"text-red-500":"text-amber-600"}`}>{item.qty===0?"OUT":"Low"}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className={`text-xs ${dark?"text-gray-400":"text-gray-600"}`}>Balance: <b>{item.qty} units</b></span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${dark?"bg-red-900/50 text-red-300":"bg-red-100 text-red-700"}`}>⚠ {item.suggest}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard title="Inventory Health Score" subtitle="AI-scored across 6 operational dimensions from live data" icon={FiPieChart} iconColor="bg-purple-500" dark={dark} extra={<LiveBadge/>}>
          <ResponsiveContainer width="100%" height={240}>
            <RadarChart data={radarData}>
              <PolarGrid stroke={dark?"#374151":"#e5e7eb"}/>
              <PolarAngleAxis dataKey="metric" tick={{fontSize:10,fill:dark?"#9ca3af":"#6b7280"}}/>
              <Radar name="Score" dataKey="score" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.3}/>
              <Tooltip {...chartTooltipStyle(dark)} formatter={v=>[`${v}/100`,"Score"]}/>
            </RadarChart>
          </ResponsiveContainer>
        </SectionCard>
      </div>

      <SectionCard title="Product Sales Velocity" subtitle="Units sold per day — identifies fast and slow movers from your invoice data" icon={FiTrendingUp} iconColor="bg-green-500" dark={dark} badge="AI Ranked" extra={<LiveBadge/>}>
        {productVelocity.length===0?<NoData dark={dark}/>:(
          <div className="space-y-3">
            {productVelocity.map((p,i)=>(
              <div key={i} className={`flex items-center gap-4 p-3 rounded-lg ${dark?"bg-gray-700/30":"bg-gray-50"}`}>
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{backgroundColor:COLORS[i%COLORS.length]}}>{i+1}</div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${dark?"text-white":"text-gray-800"}`}>{p.name}</p>
                  <div className="mt-1 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700">
                    <div className="h-1.5 rounded-full" style={{width:`${Math.round((p.velocity/(productVelocity[0].velocity||1))*100)}%`,backgroundColor:COLORS[i%COLORS.length]}}/>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className={`text-sm font-bold ${dark?"text-white":"text-gray-900"}`}>{p.velocity} <span className={`text-xs font-normal ${dark?"text-gray-400":"text-gray-500"}`}>units/day</span></p>
                  <p className={`text-xs ${dark?"text-gray-400":"text-gray-500"}`}>{fmtN(p.totalQty)} total · {fmt(p.revenue)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const AIInsights = () => {
  const { darkMode } = useSelector((state) => state.ui);
  const dark = !!darkMode;
  const [activeTab, setActiveTab] = useState(0);
  const { invoices, products, customers, loading, errors, lastUpdated, reload } = usePOSData();

  const tabs = [
    {label:"Sales Forecasting",  icon:FiTrendingUp, color:"blue"  },
    {label:"Sales Analysis",     icon:FiBarChart2,  color:"purple"},
    {label:"Inventory Analysis", icon:FiPackage,    color:"green" },
  ];

  return (
    <div className={`flex flex-col h-full overflow-y-auto px-1 pb-4 ${dark?"bg-gray-900 text-white":"bg-gray-50 text-gray-900"}`}>
      <div className="mb-4">
        <Breadcrumb items={[{label:"AI Insights & Analytics"}]}/>
        <div className="flex items-center justify-between mt-3 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl shadow-lg">
              <FiCpu className="w-5 h-5 text-white"/>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className={`text-xl font-bold ${dark?"text-white":"text-gray-900"}`}>AI Insights & Analytics</h1>
                <AIBadge/>
              </div>
              <p className={`text-xs flex items-center gap-1 ${dark?"text-gray-400":"text-gray-500"}`}>
                {loading ? "Loading your POS data…" : lastUpdated ? `Live POS data · Updated ${lastUpdated.toLocaleTimeString()}` : ""}
                {errors.length>0&&<span className="ml-1 text-amber-500 flex items-center gap-1"><FiWifiOff className="w-3 h-3"/>{errors.join(", ")} unavailable</span>}
              </p>
            </div>
          </div>
          <button onClick={reload} disabled={loading}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            <FiRefreshCw className={`w-4 h-4 ${loading?"animate-spin":""}`}/>
            {loading?"Loading…":"Refresh Data"}
          </button>
        </div>

        {!loading&&(
          <div className="flex flex-wrap gap-2 mt-3">
            {[
              {icon:FiShoppingBag,label:`${fmtN(invoices.length)} invoice lines (90d)`,bg:"bg-blue-100",text:"text-blue-700"},
              {icon:FiPackage,    label:`${fmtN(products.length)} active products`,    bg:"bg-green-100",text:"text-green-700"},
              {icon:FiUsers,      label:`${fmtN(customers.length)} customers`,          bg:"bg-purple-100",text:"text-purple-700"},
            ].map((pill,i)=>(
              <span key={i} className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full ${pill.bg} ${pill.text}`}>
                <pill.icon className="w-3 h-3"/>{pill.label}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className={`flex gap-1 p-1 rounded-xl mb-5 ${dark?"bg-gray-800":"bg-gray-200"}`}>
        {tabs.map((tab,i)=>{
          const Icon=tab.icon;
          const isActive=activeTab===i;
          const colorMap={blue:"bg-blue-600 text-white",purple:"bg-purple-600 text-white",green:"bg-green-600 text-white"};
          return (
            <button key={i} onClick={()=>setActiveTab(i)}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${isActive?colorMap[tab.color]:dark?"text-gray-400 hover:text-gray-200":"text-gray-500 hover:text-gray-700"}`}>
              <Icon className="w-4 h-4"/>{tab.label}
            </button>
          );
        })}
      </div>

      {activeTab===0&&<SalesForecastingTab  dark={dark} invoices={invoices} products={products}   loading={loading}/>}
      {activeTab===1&&<SalesAnalysisTab     dark={dark} invoices={invoices} customers={customers} loading={loading}/>}
      {activeTab===2&&<InventoryAnalysisTab dark={dark} invoices={invoices} products={products}   loading={loading}/>}
    </div>
  );
};

export default AIInsights;
